/*
 * Aura — app tab bar clearance, site-wide. 17 Sep 2026.
 *
 * The store runs an app-injected bottom tab bar (`ai-mobile-nav-*`,
 * position:fixed, ~73px, z-index 1000). Anything of ours pinned to the bottom
 * of a phone viewport has to be lifted clear of it or it is simply untappable —
 * that was the 14 Sep sticky-buy-bar bug, proved with elementFromPoint (5/5
 * hits on the app nav, 0/5 on the bar).
 *
 * That measurement already existed, but it lived INSIDE the product section,
 * so --aura-appnav-h was published on the PDP and nowhere else. Measured 17 Sep
 * on /collections/all-products: the mobile filter drawer's footer — the row
 * holding Apply and Remove all — sits at 759..844 with the app nav fixed over
 * 771..844, and 3/3 hit tests on that footer hit the app nav. Same bug, second
 * location, because the variable it needed was only ever set on one template.
 *
 * So the measurement moves here and runs everywhere. The app's height is not
 * knowable at build time, hence measured rather than hardcoded; CSS defaults it
 * to 0px, so uninstalling the app needs no code change.
 *
 * Desktop safety: this only ever writes a custom property, it is forced to 0px
 * above 749px, and no desktop rule reads it.
 *
 * NOTE: sections/aura-main-product.liquid still contains its own copy of this
 * measurement, feeding the same custom property with the same value. It is left
 * in place deliberately — that script is verified working and is entangled with
 * the buy bar's reveal logic, so re-plumbing it earns nothing and risks a
 * control that customers press.
 */
(function () {
  'use strict';

  var MOBILE = '(max-width: 749px)';

  function appNavHeight() {
    // Whichever bottom bar is actually on screen. From 17 Sep 2026 that is
    // normally OUR .aura-bnav (the app's is hidden in aura-bottom-nav.css), but
    // the app's is still checked first so this keeps working if that CSS is
    // ever reverted, and so the PDP — which renders no .aura-bnav — still
    // measures correctly either way.
    //
    // This must stay in step with whatever occupies the bottom of the screen:
    // the collection filter drawer's Apply button and the PDP buy bar both
    // clear themselves using --aura-appnav-h, and both were previously found
    // occluded by exactly this kind of bar.
    // Each candidate is TESTED, not just the first one that exists. The app's
    // element stays in the DOM after aura-bottom-nav.css hides it, so an
    // `a || b` fallback picks the hidden app bar, finds display:none and
    // returns 0 — leaving every control that clears the bottom of the screen
    // (the filter drawer's Apply, the PDP buy bar) sitting underneath OUR bar.
    // Measured that exact failure: --aura-appnav-h read 0px beside a visible
    // 57px bar.
    var candidates = document.querySelectorAll('[class*="ai-mobile-nav-"], .aura-bnav');
    for (var i = 0; i < candidates.length; i++) {
      var el = candidates[i];
      var cs = window.getComputedStyle(el);
      if (cs.position !== 'fixed' || cs.display === 'none' || cs.visibility === 'hidden') continue;
      var r = el.getBoundingClientRect();
      // Only a bar actually pinned to the bottom of the viewport displaces us.
      if (!r.height || r.bottom < window.innerHeight - 2) continue;
      return Math.round(r.height);
    }
    return 0;
  }

  /*
   * The sticky mobile header's height, published as --aura-header-h.
   *
   * Four separate rules pin things directly beneath the header and each one
   * hardcoded `top: 7rem`, measured when the bar was 70px. Shortening the
   * header to 48px on 17 Sep 2026 left every one of them 22px too low, and the
   * product grid scrolled through the gap — reported from a real phone, and the
   * collection filter bar was only the most visible instance; the homepage hero
   * had the identical crack.
   *
   * So the offset is measured rather than restated. A constant copied into four
   * files is a constant that will drift again the next time the bar changes.
   */
  function headerHeight() {
    var el = document.querySelector('.aura-header-section') ||
             document.querySelector('header.shopify-section');
    if (!el) return 0;
    var cs = window.getComputedStyle(el);
    // Only a PINNED header displaces anything. Where it scrolls away (desktop)
    // the dependent rules use their own offsets and this must not interfere.
    if (cs.position !== 'sticky' && cs.position !== 'fixed') return 0;
    return Math.round(el.getBoundingClientRect().height);
  }

  function sync() {
    var mobile = window.matchMedia(MOBILE).matches;
    var h = mobile ? appNavHeight() : 0;
    document.documentElement.style.setProperty('--aura-appnav-h', h + 'px');

    var hh = mobile ? headerHeight() : 0;
    // 0 would pin things flush to the top of the viewport, under the header.
    // Leaving the property unset lets each rule's own fallback apply instead,
    // which is the safe direction to fail in.
    if (hh > 0) document.documentElement.style.setProperty('--aura-header-h', hh + 'px');
    else document.documentElement.style.removeProperty('--aura-header-h');
  }

  if (document.readyState !== 'loading') sync();
  else document.addEventListener('DOMContentLoaded', sync);
  window.addEventListener('load', sync);
  // The app renders its own breakpoints, so re-measure rather than assume.
  window.addEventListener('resize', sync, { passive: true });
  window.addEventListener('orientationchange', sync, { passive: true });

  // The app block mounts after us, so the first measurement can legitimately be
  // 0. Watch for it arriving rather than guessing a timeout.
  if ('MutationObserver' in window) {
    var mo = new MutationObserver(sync);
    var start = function () { mo.observe(document.body, { childList: true, subtree: true }); };
    if (document.body) start();
    else document.addEventListener('DOMContentLoaded', start);
    // It cannot mount forever; stop watching so we are not observing the whole
    // document for the life of the page.
    window.setTimeout(function () { mo.disconnect(); sync(); }, 10000);
  }
})();
